import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { RoleBadge } from "@/components/badges";
import { NotificationBell } from "@/components/NotificationBell";
import {
  LayoutDashboard, Building2, Users, Briefcase, Ticket, Tags, Mail, ShieldCheck,
  LogOut, Moon, Sun, Menu, X, Inbox, CalendarOff,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

const MENUS = {
  super_admin: [
    { section: "Tableau de bord", items: [{ to: "/app", label: "Vue d'ensemble", icon: LayoutDashboard, end: true, testid: "nav-dashboard" }] },
    { section: "Gestion SaaS", items: [
      { to: "/app/companies", label: "Entreprises", icon: Building2, testid: "nav-companies" },
      { to: "/app/users", label: "Utilisateurs", icon: Users, testid: "nav-users" },
      { to: "/app/clients", label: "Clients entreprise", icon: Briefcase, testid: "nav-clients" },
    ]},
    { section: "Exploitation", items: [
      { to: "/app/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets" },
      { to: "/app/categories", label: "Catégories de tickets", icon: Tags, testid: "nav-categories" },
    ]},
    { section: "Administration", items: [
      { to: "/app/emails", label: "Emails & notifications", icon: Mail, testid: "nav-emails" },
      { to: "/app/demo-requests", label: "Demandes de démo", icon: Inbox, testid: "nav-demo-requests" },
      { to: "/app/logs", label: "Logs & sécurité", icon: ShieldCheck, testid: "nav-logs" },
    ]},
  ],
  company_admin: [
    { section: "Tableau de bord", items: [{ to: "/app", label: "Vue d'ensemble", icon: LayoutDashboard, end: true, testid: "nav-dashboard" }] },
    { section: "Gestion", items: [
      { to: "/app/users", label: "Utilisateurs", icon: Users, testid: "nav-users" },
      { to: "/app/clients", label: "Clients entreprise", icon: Briefcase, testid: "nav-clients" },
      { to: "/app/categories", label: "Catégories", icon: Tags, testid: "nav-categories" },
    ]},
    { section: "Exploitation", items: [
      { to: "/app/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets" },
    ]},
    { section: "Administration", items: [
      { to: "/app/emails", label: "Emails & notifications", icon: Mail, testid: "nav-emails" },
      { to: "/app/logs", label: "Logs & sécurité", icon: ShieldCheck, testid: "nav-logs" },
    ]},
  ],
  manager: [
    { section: "Tableau de bord", items: [{ to: "/app", label: "Vue d'ensemble", icon: LayoutDashboard, end: true, testid: "nav-dashboard" }] },
    { section: "Exploitation", items: [
      { to: "/app/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets" },
      { to: "/app/clients", label: "Clients entreprise", icon: Briefcase, testid: "nav-clients" },
      { to: "/app/categories", label: "Catégories", icon: Tags, testid: "nav-categories" },
    ]},
    { section: "Équipe", items: [
      { to: "/app/users", label: "Collaborateurs", icon: Users, testid: "nav-users" },
    ]},
  ],
  collaborator: [
    { section: "Tableau de bord", items: [{ to: "/app", label: "Vue d'ensemble", icon: LayoutDashboard, end: true, testid: "nav-dashboard" }] },
    { section: "Exploitation", items: [
      { to: "/app/tickets", label: "Mes tickets", icon: Ticket, testid: "nav-tickets" },
    ]},
  ],
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menu = MENUS[user?.role] || MENUS.collaborator;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-5 py-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary text-white font-heading font-bold">S</div>
        <div>
          <div className="font-heading font-semibold text-lg leading-none">Suivio<span className="text-primary">360</span></div>
          {user?.company && <div className="text-xs text-muted-foreground mt-1">{user.company.name}</div>}
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        {menu.map((section) => (
          <div key={section.section} className="mt-4 first:mt-0 border-t border-border pt-4 first:border-t-0 first:pt-0">
            <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{section.section}</div>
            {section.items.map((item) => (
              <NavLink
                key={item.to + item.label}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors duration-200",
                  isActive ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary" : "text-foreground/80 hover:bg-accent hover:text-foreground border-l-2 border-transparent"
                )}
              >
                <item.icon className="h-4 w-4" strokeWidth={2} />
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
        <div className="mt-4 border-t border-border pt-4">
          <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Mon compte</div>
          <NavLink
            to="/app/mon-absence"
            data-testid="nav-my-absence"
            onClick={() => setOpen(false)}
            className={({ isActive }) => cn(
              "flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors duration-200",
              isActive ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary" : "text-foreground/80 hover:bg-accent hover:text-foreground border-l-2 border-transparent"
            )}
          >
            <CalendarOff className="h-4 w-4" strokeWidth={2} />
            Mon absence
          </NavLink>
        </div>
      </nav>
      <div className="border-t border-border p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{user?.first_name} {user?.last_name}</div>
            <RoleBadge role={user?.role} />
          </div>
          <div className="flex gap-1">
            <NotificationBell up />
            <button data-testid="theme-toggle-button" onClick={toggle} className="rounded-sm border border-border p-2 hover:bg-accent transition-colors duration-200" title="Basculer clair/sombre">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button data-testid="logout-button" onClick={doLogout} className="rounded-sm border border-border p-2 hover:bg-primary hover:text-white transition-colors duration-200" title="Se déconnecter">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-background" data-testid="app-layout">
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-4 py-3">
        <div className="font-heading font-semibold">Suivio<span className="text-primary">360</span></div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} className="rounded-sm border border-border p-2">
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {open && <div className="lg:hidden fixed inset-0 z-30 bg-black/60" onClick={() => setOpen(false)} />}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 w-64 border-r border-border bg-card transition-transform duration-200 lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        {sidebar}
      </aside>
      <main className="overflow-x-hidden lg:pl-64">
        <div className="mx-auto max-w-7xl p-4 pb-24 sm:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
